import { MAX_REPLAY_SPEED, MIN_REPLAY_SPEED } from './Arguments';

// The shape RunDemonstration needs off a fetched demonstration Skill row.
// Kept permissive (`unknown` payloads) because the row crosses the feathers
// boundary untyped; the helper narrows it.
export interface DemonstrationSkillRow {
  skillType?: string;
  isDraft?: boolean;
  shared?: {
    recordings?: unknown;
    speed?: unknown;
    trimStartTimestamp?: unknown;
    trimEndTimestamp?: unknown;
  };
}

export interface ResolvedSkillRecording {
  recordingId: string;
  // Skill-level playback globals (`shared.speed` / trim bounds), null when
  // absent or non-numeric. Overridden per-step by a non-null Arguments field.
  speed: number | null;
  trimStartTimestamp: number | null;
  trimEndTimestamp: number | null;
}

const asNumberOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

// `shared.speed` is written by the UI within [MIN_REPLAY_SPEED,
// MAX_REPLAY_SPEED] but reaches the runner off an untyped feathers row, so a
// stale or externally-written value can sit outside that band — and the
// resolved value feeds `playbackSpeedMultiplier` directly, bypassing the bound
// the per-step Arguments schema enforces. Clamp to the nearest bound rather
// than rejecting: an out-of-range skill speed should still replay (slightly
// off the requested cadence) instead of failing the routine outright.
const clampSpeed = (speed: number | null): number | null =>
  speed === null
    ? null
    : Math.min(Math.max(speed, MIN_REPLAY_SPEED), MAX_REPLAY_SPEED);

// Validate a fetched demonstration Skill row and pick the recording the runner
// replays: the NEWEST one (`recordings.at(-1)`) — a re-record appends, so the
// last entry is the most recent demonstration. Throws a user-visible message
// when the skill is missing, is not an `ai-motion` skill, is still a draft, or
// has no recordings.
// Pure (no I/O) so the resolution contract is unit-testable apart from the
// feathers fetch in `RunDemonstrationStep.resolveRecordingId`.
export function resolveSkillRecording(
  skillId: string,
  skill: DemonstrationSkillRow | undefined,
): ResolvedSkillRecording {
  if (!skill) {
    throw new Error(`RunDemonstration: skill ${skillId} not found`);
  }

  if (skill.skillType !== 'ai-motion') {
    throw new Error(
      `RunDemonstration: skill ${skillId} has skillType=${skill.skillType}, expected 'ai-motion'`,
    );
  }

  if (skill.isDraft) {
    throw new Error(
      `RunDemonstration: skill ${skillId} is a draft; finish and save it before running`,
    );
  }

  const recordings = skill.shared?.recordings;

  const lastRecording = Array.isArray(recordings)
    ? recordings.at(-1)
    : undefined;

  const recordingId =
    typeof lastRecording === 'string' ? lastRecording : undefined;

  if (!recordingId) {
    throw new Error(`RunDemonstration: skill ${skillId} has no recordings`);
  }

  return {
    recordingId,
    speed: clampSpeed(asNumberOrNull(skill.shared?.speed)),
    trimStartTimestamp: asNumberOrNull(skill.shared?.trimStartTimestamp),
    trimEndTimestamp: asNumberOrNull(skill.shared?.trimEndTimestamp),
  };
}
